import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash, Tag } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { useOwnerFetch, OwnerUnlockPrompt } from '../lib/ownerFetch';

export default function OwnerPromoCodes() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', percent_off: '', start_date: '', end_date: '', max_uses: '', allowed_emails: '' });
  const [saving, setSaving] = useState(false);
  const { ownerFetch, unlockProps } = useOwnerFetch();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const result = await ownerFetch(() => api.get('/owner/promo-codes'));
    if (result.ok) {
      setCodes(result.data.promo_codes || []);
    } else if (result.error) {
      setLoadError(result.error);
    }
    setLoading(false);
  }, [ownerFetch]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: '', percent_off: '', start_date: '', end_date: '', max_uses: '', max_players: '', max_rounds: '', one_per_account: true, allowed_emails: '' });
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, percent_off: String(c.percent_off),
      start_date: c.start_date || '', end_date: c.end_date || '',
      max_uses: c.max_uses != null ? String(c.max_uses) : '',
      max_players: c.max_players != null ? String(c.max_players) : '',
      max_rounds: c.max_rounds != null ? String(c.max_rounds) : '',
      one_per_account: c.one_per_account ?? false,
      allowed_emails: (c.allowed_emails || []).join(', '),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        code: form.code.trim(),
        percent_off: parseInt(form.percent_off) || 0,
        start_date: form.start_date,
        end_date: form.end_date,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_players: form.max_players ? parseInt(form.max_players) : null,
        max_rounds: form.max_rounds ? parseInt(form.max_rounds) : null,
        one_per_account: !!form.one_per_account,
        allowed_emails: form.allowed_emails.split(/[,\n]/).map(e => e.trim()).filter(Boolean),
      };
      if (editing) {
        await api.put(`/owner/promo-codes/${editing.id}`, body);
        toast.success('Code updated');
      } else {
        await api.post('/owner/promo-codes', body);
        toast.success('Code created');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this promo code?')) return;
    try {
      await api.delete(`/owner/promo-codes/${id}`);
      toast.success('Code deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="owner-promo-codes">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate('/home')} className="text-white p-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">Promo Codes</h1></div>
        <Button onClick={openCreate} size="sm" className="bg-[#1B4332] text-white h-8 rounded-lg gap-1" data-testid="create-promo-btn">
          <Plus className="w-4 h-4" weight="bold" /> New
        </Button>
      </div>

      <div className="px-4 pt-4 max-w-[900px] mx-auto space-y-3">
        <OwnerUnlockPrompt {...unlockProps} onSuccess={load} />
        {loadError ? (
          <Card className="p-8 bg-[#FFEBEE] border-[#EF9A9A] rounded-xl text-center" data-testid="load-error">
            <p className="text-[#C62828] text-sm font-semibold">{loadError}</p>
            <Button onClick={load} size="sm" variant="outline" className="mt-3" data-testid="retry-btn">Retry</Button>
          </Card>
        ) : codes.length === 0 ? (
          <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center">
            <p className="text-[#455561] text-sm">No promo codes yet</p>
          </Card>
        ) : codes.map(c => (
          <Card key={c.id} className={`bg-white border-[#E1E6E4] rounded-xl overflow-hidden ${c.deleted_at ? 'opacity-50' : ''}`}
            data-testid={`promo-row-${c.id}`}>
            <div className="px-4 py-3 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-[#1D242B] font-mono">{c.code}</span>
                  <Badge className="bg-[#1B4332] text-white text-[10px]">{c.percent_off}% off</Badge>
                  {c.deleted_at && <Badge className="bg-[#FFEBEE] text-[#C62828] text-[10px]">Deleted</Badge>}
                </div>
                <p className="text-[10px] text-[#455561] mt-0.5">
                  {c.start_date} → {c.end_date} · {c.uses_count}{c.max_uses != null ? `/${c.max_uses}` : ''} uses
                  · {c.events_used} events · £{((c.total_discount_pence || 0) / 100).toFixed(2)} discounted
                </p>
                {c.allowed_emails?.length > 0 && (
                  <p className="text-[9px] text-[#8A9BA3] mt-0.5 truncate">Restricted: {c.allowed_emails.join(', ')}</p>
                )}
                {(c.max_players != null || c.max_rounds != null || c.one_per_account) && (
                  <p className="text-[9px] text-[#1B4332] font-semibold mt-0.5" data-testid={`promo-limits-${c.id}`}>
                    {[
                      c.max_players != null ? `Max ${c.max_players} player${c.max_players === 1 ? '' : 's'}` : null,
                      c.max_rounds != null ? `${c.max_rounds} round${c.max_rounds === 1 ? '' : 's'}` : null,
                      c.one_per_account ? '1 per account' : null,
                    ].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              {!c.deleted_at && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-[#F4F6F5]" data-testid={`edit-promo-${c.id}`}>
                    <Pencil className="w-4 h-4 text-[#455561]" />
                  </button>
                  <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg hover:bg-[#FFEBEE]" data-testid={`delete-promo-${c.id}`}>
                    <Trash className="w-4 h-4 text-[#D32F2F]" />
                  </button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="promo-modal">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Promo Code' : 'New Promo Code'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Code</label>
              <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                placeholder="e.g. SUMMER25" className="font-mono" data-testid="promo-input-code" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Percent Off (1–100)</label>
              <Input type="number" min={1} max={100} value={form.percent_off}
                onChange={e => setForm(f => ({ ...f, percent_off: e.target.value }))}
                data-testid="promo-input-percent" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#455561] block mb-1">Start Date</label>
                <Input type="date" value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                  data-testid="promo-input-start" />
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#455561] block mb-1">End Date</label>
                <Input type="date" value={form.end_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  data-testid="promo-input-end" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Max Uses (blank = unlimited)</label>
              <Input type="number" min={1} value={form.max_uses}
                onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                data-testid="promo-input-max" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#455561] block mb-1">Max Players (blank = no limit)</label>
                <Input type="number" min={1} value={form.max_players}
                  onChange={e => setForm(f => ({ ...f, max_players: e.target.value }))}
                  placeholder="e.g. 12"
                  data-testid="promo-input-max-players" />
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#455561] block mb-1">Max Rounds (blank = no limit)</label>
                <Input type="number" min={1} value={form.max_rounds}
                  onChange={e => setForm(f => ({ ...f, max_rounds: e.target.value }))}
                  placeholder="e.g. 1"
                  data-testid="promo-input-max-rounds" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="promo-one-per-account">
              <input type="checkbox" checked={!!form.one_per_account}
                onChange={e => setForm(f => ({ ...f, one_per_account: e.target.checked }))}
                className="w-4 h-4 rounded border-[#E1E6E4] text-[#1B4332] focus:ring-[#1B4332]" />
              <span className="text-xs font-semibold text-[#455561]">One use per organiser account</span>
            </label>
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Allowed Emails (comma-separated, blank = anyone)</label>
              <textarea value={form.allowed_emails}
                onChange={e => setForm(f => ({ ...f, allowed_emails: e.target.value }))}
                className="w-full h-20 px-3 py-2 rounded-lg border border-[#E1E6E4] text-sm resize-none"
                placeholder="a@example.com, b@example.com"
                data-testid="promo-input-emails" />
            </div>
            <Button onClick={handleSave} disabled={saving}
              className="w-full bg-[#1B4332] text-white h-10 rounded-lg" data-testid="promo-save-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
